import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { authHeaders } from '../utils/api';
import {
  Building2, Truck, Users, ClipboardList, Clock, AlertTriangle,
  ArrowRight, UserCog, Power, Trash2, RotateCcw,
  ShieldAlert, Plus, X, Loader2, Search, Image as ImageIcon, Upload, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATES = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

// === MASKS ===
function maskCpfCnpj(v) {
  const d = v.replace(/\D/g, '');
  if (d.length <= 11) return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}
function maskPhone(v) {
  const d = v.replace(/\D/g, '');
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}
function maskCep(v) { return v.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9); }
function isValidDoc(v) { const d = v.replace(/\D/g, ''); return d.length === 11 || d.length === 14; }

// === CONFIRM DIALOG ===
function ConfirmDialog({ open, title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={onCancel}>
      <div className="bg-[#1A1A1A] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <ShieldAlert size={24} className={confirmColor === 'red' ? 'text-red-400' : 'text-yellow-500'} />
          <h3 className="text-lg font-bold text-white">{title}</h3>
        </div>
        <p className="text-gray-300 text-sm mb-6 leading-relaxed">{message}</p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} disabled={loading} data-testid="confirm-dialog-cancel"
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors">Cancelar</button>
          <button onClick={onConfirm} disabled={loading} data-testid="confirm-dialog-confirm"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${confirmColor === 'red' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-yellow-500 hover:bg-yellow-400 text-black'}`}>
            {loading ? 'Processando...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// === NEW TENANT FORM ===
function NewTenantForm({ open, onClose, onSuccess }) {
  const emptyForm = { document: '', razao_social: '', nome_fantasia: '', website: '', email: '', admin_name: '', phone: '', street: '', number: '', zip_code: '', city: '', state: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const docValid = isValidDoc(form.document);
  const fieldsEnabled = docValid;

  const handleChange = (field, value) => {
    if (field === 'document') value = maskCpfCnpj(value);
    else if (field === 'phone') value = maskPhone(value);
    else if (field === 'zip_code') value = maskCep(value);
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!docValid) return;
    if (!form.razao_social.trim()) { toast.error('Razao Social e obrigatoria'); return; }
    if (!form.email.trim()) { toast.error('E-mail e obrigatorio'); return; }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tenants`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const data = await response.json();
      if (response.ok) {
        setResult(data);
        toast.success(`Empresa "${data.name}" criada com sucesso!`);
        onSuccess();
      } else {
        toast.error(data.detail || 'Erro ao criar empresa');
      }
    } catch { toast.error('Erro de conexao'); }
    finally { setSaving(false); }
  };

  const handleClose = () => {
    setForm(emptyForm);
    setResult(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 overflow-y-auto py-4" onClick={handleClose}>
      <div className="bg-[#1A1A1A] border border-gray-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Building2 size={20} className="text-yellow-500" /> Nova Empresa
          </h2>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 text-center">
              <h3 className="text-green-400 text-lg font-bold mb-2">Empresa Criada com Sucesso!</h3>
              <p className="text-gray-300 text-sm">{result.name}</p>
            </div>
            {result.admin && (
              <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl p-5 space-y-2">
                <p className="text-gray-400 text-sm font-medium">Credenciais do Administrador:</p>
                <p className="text-white"><span className="text-gray-500">E-mail:</span> {result.admin.email}</p>
                <p className="text-white"><span className="text-gray-500">Senha temporaria:</span> <code className="bg-gray-800 px-2 py-0.5 rounded text-yellow-500">{result.temp_password}</code></p>
                <p className="text-yellow-500 text-xs mt-3">Um e-mail de boas-vindas foi enviado com estas credenciais.</p>
              </div>
            )}
            <button onClick={handleClose} data-testid="close-success-btn"
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors">
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">CPF ou CNPJ *</label>
              <input
                type="text" value={form.document} onChange={e => handleChange('document', e.target.value)}
                placeholder="Digite o CPF ou CNPJ" maxLength={18}
                data-testid="tenant-form-document"
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none text-lg tracking-wider"
              />
              {form.document && !docValid && (
                <p className="text-red-400 text-xs mt-1">Preencha um CPF (11 digitos) ou CNPJ (14 digitos) valido para continuar</p>
              )}
              {docValid && <p className="text-green-400 text-xs mt-1">Documento valido - preencha os demais campos</p>}
            </div>

            <fieldset disabled={!fieldsEnabled} className={!fieldsEnabled ? 'opacity-40 pointer-events-none' : ''}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Razao Social *" field="razao_social" value={form.razao_social} onChange={handleChange} placeholder="Razao Social da empresa" testId="tenant-form-razao" />
                  <FormField label="Nome Fantasia" field="nome_fantasia" value={form.nome_fantasia} onChange={handleChange} placeholder="Nome Fantasia" testId="tenant-form-fantasia" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Site" field="website" value={form.website} onChange={handleChange} placeholder="www.exemplo.com.br" testId="tenant-form-website" />
                  <FormField label="Telefone" field="phone" value={form.phone} onChange={handleChange} placeholder="(00) 00000-0000" testId="tenant-form-phone" mask="phone" />
                </div>

                <div className="pt-2 border-t border-gray-800">
                  <p className="text-yellow-500 text-xs font-medium mb-3">Responsavel / Administrador da Empresa</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Nome do Responsavel" field="admin_name" value={form.admin_name} onChange={handleChange} placeholder="Nome completo" testId="tenant-form-admin-name" />
                    <FormField label="E-mail Principal *" field="email" value={form.email} onChange={handleChange} placeholder="email@empresa.com.br" type="email" testId="tenant-form-email" />
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-800">
                  <p className="text-gray-500 text-xs font-medium mb-3">Endereco</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <FormField label="Rua" field="street" value={form.street} onChange={handleChange} placeholder="Rua, Avenida..." testId="tenant-form-street" />
                    </div>
                    <FormField label="Numero" field="number" value={form.number} onChange={handleChange} placeholder="No" testId="tenant-form-number" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <FormField label="CEP" field="zip_code" value={form.zip_code} onChange={handleChange} placeholder="00000-000" testId="tenant-form-cep" mask="cep" />
                    <FormField label="Cidade" field="city" value={form.city} onChange={handleChange} placeholder="Cidade" testId="tenant-form-city" />
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1.5">Estado</label>
                      <select value={form.state} onChange={e => handleChange('state', e.target.value)}
                        data-testid="tenant-form-state"
                        className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:outline-none">
                        <option value="">UF</option>
                        {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={handleClose} className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={saving || !fieldsEnabled} data-testid="tenant-form-submit"
                className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {saving ? <><Loader2 size={18} className="animate-spin" /> Criando...</> : <><Plus size={18} /> Criar Empresa</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// === TENANT SETTINGS MODAL (Bloco 10 — Master only: logo + branding) ===
function TenantSettingsModal({ open, tenant, onClose, onSaved }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (open && tenant) {
      setLogoUrl(tenant.logo_url || null);
      setPreviewUrl(null);
    }
  }, [open, tenant]);

  if (!open || !tenant) return null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    // Validações client-side
    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato inválido. Use apenas PNG ou JPG.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo excede o limite de 2MB.');
      return;
    }

    // Preview imediato
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/tenants/${tenant.id}/logo`, {
        method: 'POST',
        headers: authHeaders(),  // Não setar Content-Type — browser cuida do multipart boundary
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.logo_url) {
        setLogoUrl(data.logo_url);
        setPreviewUrl(null);
        toast.success('Logomarca atualizada com sucesso.');
        onSaved && onSaved();
      } else {
        setPreviewUrl(null);
        toast.error(data.detail || 'Erro ao enviar logomarca.');
      }
    } catch (err) {
      setPreviewUrl(null);
      toast.error('Erro de conexão ao enviar logomarca.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const res = await fetch(`${API_URL}/api/tenants/${tenant.id}/logo`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        setLogoUrl(null);
        setPreviewUrl(null);
        toast.success('Logomarca removida.');
        onSaved && onSaved();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao remover.');
      }
    } catch {
      toast.error('Erro de conexão.');
    } finally {
      setRemoving(false);
    }
  };

  const displayUrl = previewUrl || logoUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="tenant-settings-modal">
      <div className="bg-[#1A1A1A] border border-gray-700 rounded-xl p-6 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
              <Settings size={18} className="text-yellow-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Configurações do Tenant</h3>
              <p className="text-gray-400 text-xs">{tenant.name}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-tenant-settings"
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <ImageIcon size={14} className="text-yellow-500" /> Logomarca para relatórios em PDF
            </h4>
            <p className="text-xs text-gray-400 mb-3">
              A logomarca aparece no cabeçalho de TODOS os relatórios em PDF deste tenant.
              Formato aceito: PNG ou JPG, até 2MB. Recomendado: imagem com fundo transparente,
              proporção próxima de 3:1 (largura:altura).
            </p>

            <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 bg-[#0A0A0A]">
              {displayUrl ? (
                <div className="flex items-start gap-4">
                  <div className="bg-white rounded-lg p-3 shadow-inner flex-shrink-0">
                    <img
                      src={displayUrl}
                      alt="Logo do tenant"
                      data-testid="tenant-logo-preview"
                      className="h-20 max-w-[160px] object-contain"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <p className="text-xs text-gray-300">Logo atual aplicada nos relatórios.</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <label data-testid="tenant-logo-replace-btn"
                        className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold rounded-lg transition-colors">
                        <Upload size={14} />
                        {uploading ? 'Enviando...' : 'Substituir'}
                        <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleFile}
                          disabled={uploading || removing} className="hidden" data-testid="tenant-logo-input" />
                      </label>
                      <button onClick={handleRemove} disabled={uploading || removing} data-testid="tenant-logo-remove-btn"
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm rounded-lg transition-colors">
                        <Trash2 size={14} /> {removing ? 'Removendo...' : 'Remover'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <label data-testid="tenant-logo-upload-label"
                  className="flex flex-col items-center justify-center py-6 cursor-pointer">
                  <Upload size={32} className="text-gray-500 mb-2" />
                  <p className="text-sm text-gray-300 font-medium">
                    {uploading ? 'Enviando...' : 'Clique para enviar a logomarca'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">PNG ou JPG • até 2MB</p>
                  <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleFile}
                    disabled={uploading} className="hidden" data-testid="tenant-logo-input" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-800">
          <button onClick={onClose} data-testid="tenant-settings-close-btn"
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}


function FormField({ label, field, value, onChange, placeholder, type = 'text', testId, mask }) {
  const handleInput = (e) => {
    let v = e.target.value;
    if (mask === 'phone') v = maskPhone(v);
    else if (mask === 'cep') v = maskCep(v);
    onChange(field, v);
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-1.5">{label}</label>
      <input type={type} value={value} onChange={handleInput} placeholder={placeholder}
        data-testid={testId}
        className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none" />
    </div>
  );
}

// === TENANT TABLE ===
function TenantTable({ title, tenants, onAccess, onToggle, onDelete, onSettings, inactive, showActions }) {
  if (tenants.length === 0) return null;
  return (
    <div className={`bg-[#111] border rounded-xl overflow-hidden ${inactive ? 'border-gray-800/50 opacity-80' : 'border-gray-800'}`}>
      <div className="px-4 lg:px-6 py-4 border-b border-gray-800 flex items-center gap-3">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <span className="text-xs px-2 py-0.5 bg-gray-800 text-gray-400 rounded-full">{tenants.length}</span>
      </div>
      {/* Desktop */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400">
              <th className="text-left px-6 py-3 font-medium">Empresa</th>
              <th className="text-center px-3 py-3 font-medium">Maquinas</th>
              <th className="text-center px-3 py-3 font-medium">Operadores</th>
              <th className="text-center px-3 py-3 font-medium">Usuarios</th>
              <th className="text-center px-3 py-3 font-medium">O.S.</th>
              <th className="text-center px-3 py-3 font-medium">Horas</th>
              <th className="text-center px-3 py-3 font-medium">Alertas</th>
              {showActions && <th className="text-right px-6 py-3 font-medium">Acoes</th>}
            </tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${inactive ? 'bg-gray-700/30' : 'bg-yellow-500/10'}`}>
                      <Building2 size={16} className={inactive ? 'text-gray-500' : 'text-yellow-500'} />
                    </div>
                    <div>
                      <p className={`font-medium ${inactive ? 'text-gray-400' : 'text-white'}`}>{t.name}</p>
                      <p className="text-gray-500 text-xs">{t.document}</p>
                    </div>
                    {t.is_demo && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/10 text-yellow-500 rounded">Demo</span>}
                    {inactive && <span className="text-xs px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded">Inativa</span>}
                  </div>
                </td>
                <td className="text-center px-3 py-4 text-gray-300">{t.machines}</td>
                <td className="text-center px-3 py-4 text-gray-300">{t.operators}</td>
                <td className="text-center px-3 py-4 text-gray-300">{t.users}</td>
                <td className="text-center px-3 py-4 text-gray-300">{t.open_orders}/{t.closed_orders}</td>
                <td className="text-center px-3 py-4 text-gray-300">{t.total_hours.toLocaleString('pt-BR')}</td>
                <td className="text-center px-3 py-4">
                  {t.maintenance_alerts > 0 ? <span className="inline-flex items-center gap-1 text-red-400 text-xs font-medium"><AlertTriangle size={12} /> {t.maintenance_alerts}</span> : <span className="text-gray-600">0</span>}
                </td>
                {showActions && (
                  <td className="text-right px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {!inactive && onAccess && (
                        <button onClick={() => onAccess(t.id)} data-testid={`go-to-tenant-${t.id}`}
                          className="inline-flex items-center gap-1 text-yellow-500 hover:text-yellow-400 text-xs transition-colors">Acessar <ArrowRight size={12} /></button>
                      )}
                      {onSettings && (
                        <button onClick={() => onSettings(t)} data-testid={`settings-tenant-${t.id}`}
                          title="Configurações (Logomarca)"
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-700 hover:text-yellow-500 transition-colors">
                          <Settings size={15} />
                        </button>
                      )}
                      {onToggle && (
                        <button onClick={() => onToggle(t)} data-testid={`toggle-tenant-${t.id}`}
                          title={inactive ? 'Reativar' : 'Inativar'}
                          className={`p-1.5 rounded-lg transition-colors ${inactive ? 'text-green-400 hover:bg-green-500/10' : 'text-gray-400 hover:bg-gray-700 hover:text-yellow-500'}`}>
                          {inactive ? <RotateCcw size={15} /> : <Power size={15} />}
                        </button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(t)} data-testid={`delete-tenant-${t.id}`}
                          title="Excluir" className="p-1.5 rounded-lg text-gray-500 hover:bg-red-500/10 hover:text-red-400 transition-colors">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile */}
      <div className="lg:hidden divide-y divide-gray-800/50">
        {tenants.map(t => (
          <div key={t.id} className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 size={16} className={inactive ? 'text-gray-500' : 'text-yellow-500'} />
                <span className={`font-medium text-sm ${inactive ? 'text-gray-400' : 'text-white'}`}>{t.name}</span>
                {t.is_demo && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/10 text-yellow-500 rounded">Demo</span>}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div className="bg-[#0A0A0A] rounded-lg p-2"><p className="text-gray-500">Maquinas</p><p className="text-white font-semibold">{t.machines}</p></div>
              <div className="bg-[#0A0A0A] rounded-lg p-2"><p className="text-gray-500">Operadores</p><p className="text-white font-semibold">{t.operators}</p></div>
              <div className="bg-[#0A0A0A] rounded-lg p-2"><p className="text-gray-500">Horas</p><p className="text-white font-semibold">{t.total_hours.toLocaleString('pt-BR')}</p></div>
              <div className="bg-[#0A0A0A] rounded-lg p-2"><p className="text-gray-500">Alertas</p><p className={`font-semibold ${t.maintenance_alerts > 0 ? 'text-red-400' : 'text-gray-500'}`}>{t.maintenance_alerts}</p></div>
            </div>
            {showActions && (
              <div className="flex items-center gap-2 pt-1">
                {!inactive && onAccess && <button onClick={() => onAccess(t.id)} className="flex-1 text-center py-2 bg-yellow-500/10 text-yellow-500 rounded-lg text-xs font-medium">Acessar</button>}
                <button onClick={() => onToggle(t)} className={`flex-1 text-center py-2 rounded-lg text-xs font-medium ${inactive ? 'bg-green-500/10 text-green-400' : 'bg-gray-700 text-gray-300'}`}>{inactive ? 'Reativar' : 'Inativar'}</button>
                <button onClick={() => onDelete(t)} className="py-2 px-3 bg-red-500/10 text-red-400 rounded-lg text-xs"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// === MAIN COMPONENT ===
export default function MasterTenants() {
  const { enterTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false });
  const [actionLoading, setActionLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [search, setSearch] = useState('');
  const [settingsTenant, setSettingsTenant] = useState(null);

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenants/overview/all`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
      } else {
        toast.error('Erro ao carregar empresas');
      }
    } catch { toast.error('Erro de conexao'); }
    finally { setLoading(false); }
  };

  const handleGoToTenant = (tenantId) => { enterTenant(tenantId); navigate('/dashboard'); };

  const handleToggleStatus = (tenant) => {
    const isActive = tenant.status === 'active';
    setDialog({
      open: true, title: isActive ? 'Inativar Empresa' : 'Reativar Empresa',
      message: isActive
        ? `Deseja inativar "${tenant.name}"? A empresa ficara oculta para todos os usuarios, mas todos os dados serao preservados. Voce pode reativa-la a qualquer momento.`
        : `Deseja reativar "${tenant.name}"? A empresa voltara a ficar visivel para seus usuarios.`,
      confirmLabel: isActive ? 'Inativar' : 'Reativar',
      confirmColor: isActive ? 'red' : 'yellow',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/tenants/${tenant.id}/toggle-status`, { method: 'PATCH', headers: authHeaders() });
          if (response.ok) { const r = await response.json(); toast.success(r.status === 'active' ? `"${r.name}" reativada` : `"${r.name}" inativada`); fetchTenants(); }
          else { const err = await response.json(); toast.error(err.detail || 'Erro'); }
        } catch { toast.error('Erro de conexao'); }
        finally { setActionLoading(false); setDialog({ open: false }); }
      }
    });
  };

  const handleDelete = async (tenant) => {
    try {
      const response = await fetch(`${API_URL}/api/tenants/${tenant.id}/data-count`, { headers: authHeaders() });
      if (!response.ok) { toast.error('Erro ao verificar dados'); return; }
      const counts = await response.json();
      if (!counts.can_delete) {
        const parts = [];
        if (counts.machines > 0) parts.push(`${counts.machines} maquina(s)`);
        if (counts.operators > 0) parts.push(`${counts.operators} operador(es)`);
        if (counts.work_orders > 0) parts.push(`${counts.work_orders} O.S.`);
        if (counts.users > 0) parts.push(`${counts.users} usuario(s)`);
        if (counts.locations > 0) parts.push(`${counts.locations} local(is)`);
        toast.error(`Nao e possivel excluir: empresa possui ${parts.join(', ')}. Inative ao inves de excluir.`);
        return;
      }
      setDialog({
        open: true, title: 'Excluir Empresa Permanentemente',
        message: `ATENCAO: Esta acao e IRREVERSIVEL. Deseja excluir permanentemente "${tenant.name}"?`,
        confirmLabel: 'Excluir Permanentemente', confirmColor: 'red',
        onConfirm: async () => {
          setActionLoading(true);
          try {
            const res = await fetch(`${API_URL}/api/tenants/${tenant.id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) { toast.success(`"${tenant.name}" excluida`); fetchTenants(); }
            else { const err = await res.json(); toast.error(err.detail || 'Erro'); }
          } catch { toast.error('Erro de conexao'); }
          finally { setActionLoading(false); setDialog({ open: false }); }
        }
      });
    } catch { toast.error('Erro de conexao'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const filtered = search.trim()
    ? tenants.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || (t.document || '').toLowerCase().includes(search.toLowerCase()))
    : tenants;
  const activeTenants = filtered.filter(t => t.status === 'active');
  const inactiveTenants = filtered.filter(t => t.status === 'inactive');

  return (
    <div data-testid="master-tenants" className="space-y-6">
      <ConfirmDialog open={dialog.open} title={dialog.title} message={dialog.message}
        confirmLabel={dialog.confirmLabel} confirmColor={dialog.confirmColor}
        onConfirm={dialog.onConfirm} onCancel={() => !actionLoading && setDialog({ open: false })} loading={actionLoading} />
      <NewTenantForm open={showNewForm} onClose={() => setShowNewForm(false)} onSuccess={fetchTenants} />
      <TenantSettingsModal open={!!settingsTenant} tenant={settingsTenant} onClose={() => setSettingsTenant(null)} onSaved={fetchTenants} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Empresas</h1>
          <p className="text-gray-400 text-sm mt-1">Gestao de tenants e empresas cadastradas</p>
        </div>
        <button onClick={() => setShowNewForm(true)} data-testid="new-tenant-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors shrink-0">
          <Plus size={18} /> Nova Empresa
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
          <p className="text-gray-400 text-xs mb-1">Total</p>
          <p className="text-white text-2xl font-bold">{tenants.length}</p>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
          <p className="text-green-400 text-xs mb-1">Ativas</p>
          <p className="text-white text-2xl font-bold">{tenants.filter(t => t.status === 'active').length}</p>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
          <p className="text-red-400 text-xs mb-1">Inativas</p>
          <p className="text-white text-2xl font-bold">{tenants.filter(t => t.status === 'inactive').length}</p>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
          <p className="text-yellow-500 text-xs mb-1">Demo</p>
          <p className="text-white text-2xl font-bold">{tenants.filter(t => t.is_demo).length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar empresa por nome ou documento..."
          data-testid="tenant-search-input"
          className="w-full pl-10 pr-4 py-3 bg-[#111] border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
        />
      </div>

      {/* Tenant tables */}
      <TenantTable title="Empresas Ativas" tenants={activeTenants} onAccess={handleGoToTenant} onToggle={handleToggleStatus} onDelete={handleDelete} onSettings={setSettingsTenant} showActions />
      <TenantTable title="Empresas Inativas" tenants={inactiveTenants} onToggle={handleToggleStatus} onDelete={handleDelete} onSettings={setSettingsTenant} inactive showActions />

      {filtered.length === 0 && search.trim() && (
        <div className="text-center py-12">
          <Search size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Nenhuma empresa encontrada para "{search}"</p>
        </div>
      )}
    </div>
  );
}
